import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { BarChart } from '@m/alchemy-ui/BarChart';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { Select } from '@m/alchemy-ui/Select';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import {
  type ChallengesQuery,
  type CoauthorIdeasQuery,
  type FilteredUsersQuery,
  GET_CHALLENGES,
  GET_COAUTHOR_IDEAS,
  GET_COUNTRIES,
  GET_STATUS_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetIdeaStatusesSummaryQuery,
  type GetStatusIdeasAndCountQuery
} from '../../graphql/queries';

import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { GET_IDEA_STATUSES_SUMMARY } from '../../graphql/queries/stat';
import { decodeHtml, getExportIdeasUrl } from '../../helpers';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';

import * as Styled from './components/SearchFilters.styled';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';
import { CustomFilters } from './CustomFilters';
import { type ChallengeListItemType, type IdeaStatusSummaryItemType, type UserFieldType } from './types';
import { ChartExportMenu, IdeasExportButton, IdeasTable, TableView } from './components';

import { IDEAS_DEFAULT_OFFSET, IDEAS_DEFAULT_SORT, IDEAS_PER_PAGE } from './constants';

import { AlchemySortDirectionType, ChallengeSelectionType, CoreApiSortDirectionType } from './enums';

export const IdeasByStatusStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();

  const community = useContext(CommunityContext);

  const { data: communityUserFields } = useUserFields();

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);
  const [countries, setCountries] = useState<string[]>([]);

  const [challenge, setChallenge] = useState<string>(ChallengeSelectionType.PUBLISHED);
  const [status, setStatus] = useState<string | undefined>();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: countryList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    countryList?.community && setCountries(countryList.community?.countries as string[]);
  }, [countryList]);

  const { loading: loadingChallenges, data: challengeList } = useQuery<ChallengesQuery>(GET_CHALLENGES, {
    variables: { order: ['title'] },
    fetchPolicy: 'cache-first'
  });

  const { data: customFiltersUserIds } = useQuery<FilteredUsersQuery>(GET_USERFIELDS_FILTERED_USERIDS, {
    variables: { filters: { userFields: JSON.stringify(userFieldOptions) } },
    fetchPolicy: 'cache-first'
  });

  const { data: coauthorIdeaIds } = useQuery<CoauthorIdeasQuery>(GET_COAUTHOR_IDEAS, {
    variables: {
      customFiltersUserIds: customFiltersUserIds?.filteredUsers || [],
      filters: { userFields: JSON.stringify(userFieldOptions) }
    },
    skip: !customFiltersUserIds
  });

  const [ideaStatusesSummaryQueryVariables, setIdeaStatusesSummaryQueryVariables] = useState({
    challengeSelection: challenge,
    filters: { userFields: JSON.stringify(userFieldOptions) }
  });

  const { data: chartData, loading: loadingChart } = useQuery<GetIdeaStatusesSummaryQuery>(GET_IDEA_STATUSES_SUMMARY, {
    variables: ideaStatusesSummaryQueryVariables
  });

  const [statusIdeasQueryVariables, setStatusIdeasQueryVariables] = useState({
    challengeSelection: challenge,
    statusSelection: status,
    filters: { userFields: JSON.stringify(userFieldOptions) },
    order: [IDEAS_DEFAULT_SORT],
    offset: IDEAS_DEFAULT_OFFSET
  });

  const isStatusSelected = useMemo(
    () => statusIdeasQueryVariables.statusSelection !== undefined,
    [statusIdeasQueryVariables]
  );

  const { data: ideasData, loading: loadingIdeas } = useQuery<GetStatusIdeasAndCountQuery>(GET_STATUS_IDEAS_AND_COUNT, {
    variables: statusIdeasQueryVariables,
    skip: !isStatusSelected
  });

  useEffect(() => {
    setStatusIdeasQueryVariables({
      challengeSelection: challenge,
      statusSelection: status,
      filters: { userFields: JSON.stringify(userFieldOptions) },
      order: [IDEAS_DEFAULT_SORT],
      offset: IDEAS_DEFAULT_OFFSET
    });
  }, [challenge, setStatusIdeasQueryVariables, status, userFieldOptions]);

  const sortIdeas = (column: string) => {
    const { order } = statusIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    const newOrder = [
      [
        column,
        column === sortColumn && sortDirection === CoreApiSortDirectionType.ASC
          ? CoreApiSortDirectionType.DESC
          : CoreApiSortDirectionType.ASC
      ]
    ];

    setStatusIdeasQueryVariables({
      ...statusIdeasQueryVariables,
      order: newOrder
    });
  };

  const getColumnSortDirection = (column: string) => {
    const { order } = statusIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    return sortColumn === column
      ? sortDirection === CoreApiSortDirectionType.DESC
        ? AlchemySortDirectionType.DESC
        : AlchemySortDirectionType.ASC
      : 'none';
  };

  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });
  const statusLabel = intl.formatMessage({ id: 'status', defaultMessage: 'Status' });

  const windowWidth = useWindowWidth();

  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  const chartSummary = useMemo(() => chartData?.ideaStatusesSummary ?? [], [chartData?.ideaStatusesSummary]);

  const chartSeries = useMemo(
    () => [
      {
        id: ideasLabel,
        data: chartSummary.map(({ ideaCount, statusId, statusName }: IdeaStatusSummaryItemType) => ({
          x: `${decodeHtml(statusName)}_${statusId}`,
          y: ideaCount ?? 0,
          statusId,
          statusName: decodeHtml(statusName)
        }))
      }
    ],
    [ideasLabel, chartSummary]
  );

  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeries.length), [chartSeries.length]);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(() => [{ label: statusLabel }, { label: ideasLabel }], [ideasLabel, statusLabel]);

  const buildTableRows = useCallback(() => {
    return chartData?.ideaStatusesSummary?.map((item) => [item.statusName, item.ideaCount]) || [];
  }, [chartData?.ideaStatusesSummary]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildTableRows() };
  }, [buildTableRows, tableColumns]);

  if ((loadingChallenges && !challengeList) || (loadingChart && !chartData)) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  const formatStatusName = (value: string | number) => {
    const dataPoint = chartSeries[0]?.data.find((d) => d.x === value);
    return dataPoint?.statusName || value;
  };

  return (
    <Grid key="ideasByStatusStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={(userField: UserFieldType) => {
          setStatus(undefined);
          setUserFieldOptions(userField);
        }}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'allPublishedChallenges', defaultMessage: 'All Published Challenges' }),
              value: ChallengeSelectionType.PUBLISHED,
              'data-testid': `challenge-${ChallengeSelectionType.PUBLISHED}`
            },
            {
              label: intl.formatMessage({ id: 'allChallenges', defaultMessage: 'All Challenges' }),
              value: ChallengeSelectionType.ALL,
              'data-testid': `challenge-${ChallengeSelectionType.ALL}`
            },
            ...((challengeList as ChallengesQuery) &&
              (challengeList?.challenges as ChallengeListItemType[]) &&
              (challengeList?.challenges as ChallengeListItemType[]).map((challenge: ChallengeListItemType) => ({
                label: challenge.title,
                value: challenge.id,
                'data-testid': `challenge-${challenge.title}`
              })))
          ]}
          onChange={(e) => {
            setChallenge(e.value);
            setStatus(undefined);
          }}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            setIdeaStatusesSummaryQueryVariables({
              challengeSelection: challenge,
              filters: { userFields: JSON.stringify(userFieldOptions) }
            });
          }}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {!dataTableViewEnabled && (
        <ChartActionsRow key="ideasByStatusExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'ideasByStatus', defaultMessage: 'Ideas by Status' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart">
        {dataTableViewEnabled ? (
          <TableView
            columns={tableColumns}
            rows={tableRows}
            formatDate={formatDate}
            onRowClick={(index: number) => {
              const statusIds = chartSummary.map((item) => item.statusId);
              setStatus(String(statusIds[index]));
            }}
            summary={intl.formatMessage({
              id: 'ideasByStatus',
              defaultMessage: 'Ideas by Status'
            })}
          />
        ) : (
          <ChartScroll width={chartScrollWidth}>
            <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
              <ThemeProvider theme={accessibleChartTheme}>
                <BarChart
                  axisX={{
                    label: intl.formatMessage({ id: 'statusLabel', defaultMessage: 'Status Label' }),
                    formatTick: formatStatusName,
                    formatTooltipValue: formatStatusName
                  }}
                  axisY={{
                    formatScreenReaderValue: (value) => `${ideasLabel}: ${value}`,
                    label: intl.formatMessage({
                      id: 'ideasByStatus',
                      defaultMessage: 'Ideas by Status'
                    }),
                    formatValue: (value) => {
                      const numericValue = Number(value);
                      return Number.isInteger(numericValue) ? numericValue : '';
                    }
                  }}
                  data={chartSeries}
                  domainScaling="from-zero"
                  hasLegend={false}
                  hasValueLabels
                  onDataClick={(dataPoint) => {
                    if (dataPoint.statusId !== undefined) {
                      setStatus(String(dataPoint.statusId));
                    }
                  }}
                />
              </ThemeProvider>
            </ChartScrollInner>
          </ChartScroll>
        )}
      </GridRow>
      {isStatusSelected && (
        <GridRow key="table" justify="center">
          {loadingIdeas && !ideasData ? (
            <Styled.LoadingContainer>
              <LoadingIndicator inline />
            </Styled.LoadingContainer>
          ) : ideasData?.statusIdeasAndCount && ideasData.statusIdeasAndCount.count > 0 ? (
            <>
              <GridCol>
                <IdeasTable
                  getColumnSortDirection={getColumnSortDirection}
                  ideasAndCount={ideasData.statusIdeasAndCount}
                  intl={intl}
                  sortIdeas={sortIdeas}
                />
              </GridCol>
              <GridCol>
                <Pagination
                  numPages={Math.ceil(ideasData.statusIdeasAndCount.count / IDEAS_PER_PAGE)}
                  activeIndex={Math.ceil(statusIdeasQueryVariables.offset / IDEAS_PER_PAGE)}
                  onPageClick={(page) => {
                    const pageIndex = page.index as number;
                    setStatusIdeasQueryVariables({
                      ...statusIdeasQueryVariables,
                      offset: pageIndex * IDEAS_PER_PAGE
                    });
                  }}
                />
              </GridCol>
            </>
          ) : (
            <ErrorMessage
              message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
              details={intl.formatMessage({
                id: 'pleaseAdjustYourSearch',
                defaultMessage: 'Please adjust your search parameters and try again.'
              })}
            />
          )}
        </GridRow>
      )}
      {isStatusSelected && ideasData?.statusIdeasAndCount && ideasData.statusIdeasAndCount.count > 0 && (
        <GridRow key="exportData">
          <IdeasExportButton
            exportUrl={getExportIdeasUrl({ challenge, status, customFiltersUserIds, coauthorIdeaIds }, community)}
            intl={intl}
          />
        </GridRow>
      )}
    </Grid>
  );
};
